import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import type { BannerResult } from '../../types/onboarding';
import { getBannerBatchStatus } from '../../services/api/onboardingService';
import { trackMetaBannerGenerated } from '../../utils/metaPixel';
import { trackXBannerGenerated } from '../../utils/xPixel';

interface BannerGrid30StepProps {
  sessionId: string;
  onContinue: () => void;
  onBack: () => void;
}

// Polling config
const POLL_INTERVAL_MS = 3000;
const GENERATION_TIMEOUT_MS = 300000; // 5 minutes

// --- Tailwind class constants (旧 styled-components) ---
// keyframes は globals.css の `obs-shimmer` / `obs-pulse-dim` / `obs-fade-in-scale` /
// `obs-fade-in-up` へ移設（`animate-fade-in-up` は tailwind.config の同内容既存定義）。

const CONTAINER = 'text-center py-4 animate-fade-in-up';

const TITLE = 'text-[1.5rem] font-bold text-gray-800 m-0 mb-2 max-[480px]:text-[1.25rem]';

const SUBTITLE = 'text-[0.9375rem] text-gray-500 m-0 mb-4';

const COUNTER_SECTION = 'mb-4';

const COUNTER_TEXT =
  'text-[1rem] font-semibold text-gray-700 mb-2 ' +
  '[&_span]:text-violet-600 [&_span]:text-[1.25rem]';

const PROGRESS_BAR_TRACK =
  'w-full max-w-[400px] h-2 bg-gray-100 rounded-[4px] overflow-hidden mx-auto';

// width は連続値なので style={{ width }} でインライン指定
const PROGRESS_BAR_FILL =
  'h-full rounded-[4px] bg-[linear-gradient(90deg,#7c3aed,#a78bfa)] bg-[length:200%_100%] ' +
  'animate-[obs-shimmer_2s_ease-in-out_infinite] transition-[width] duration-500 ease-[ease]';

const GRID =
  'grid grid-cols-6 gap-2.5 mb-6 ' +
  'max-[1024px]:grid-cols-3 max-[1024px]:gap-2 ' +
  'max-[600px]:grid-cols-2 max-[600px]:gap-2';

// `group` は DownloadButton の `${BannerCard}:hover &` を group-hover: で再現するためのマーカー
const BANNER_CARD_LOADED =
  'group rounded-[10px] overflow-hidden transition-[transform,box-shadow] duration-200 ease-[ease] ' +
  'bg-white shadow-[0_4px_12px_rgba(0,0,0,0.1)] animate-[obs-fade-in-scale_0.4s_ease-out] ' +
  'hover:-translate-y-1 hover:scale-[1.02] hover:shadow-[0_8px_24px_rgba(0,0,0,0.15)]';

const BANNER_CARD_PENDING =
  'group rounded-[10px] overflow-hidden transition-[transform,box-shadow] duration-200 ease-[ease] ' +
  'bg-white shadow-[0_2px_6px_rgba(0,0,0,0.05)]';

const IMAGE_WRAPPER = 'relative w-full aspect-square bg-gray-100 overflow-hidden';

const PLACEHOLDER_SLOT =
  'w-full h-full bg-[linear-gradient(90deg,#f3f4f6_25%,#e5e7eb_50%,#f3f4f6_75%)] ' +
  'bg-[length:200%_100%] animate-[obs-shimmer_1.5s_ease-in-out_infinite] ' +
  'flex items-center justify-center';

const PLACEHOLDER_NUMBER =
  'text-[1.25rem] font-bold text-gray-300 animate-[obs-pulse-dim_2s_ease-in-out_infinite]';

const BANNER_IMAGE =
  'w-full h-full object-cover block animate-[obs-fade-in-scale_0.5s_ease-out]';

const STYLE_BADGE =
  'py-1.5 px-2 bg-white text-[0.6875rem] font-semibold text-gray-700 text-center ' +
  'whitespace-nowrap overflow-hidden text-ellipsis';

const DOWNLOAD_BUTTON =
  'absolute top-1.5 right-1.5 w-7 h-7 rounded-md border-0 bg-white/90 text-gray-700 ' +
  'cursor-pointer flex items-center justify-center opacity-0 ' +
  'transition-[opacity,background-color] duration-200 ease-[ease] ' +
  'shadow-[0_1px_4px_rgba(0,0,0,0.1)] group-hover:opacity-100 ' +
  'hover:bg-violet-600 hover:text-white [&_svg]:w-3.5 [&_svg]:h-3.5';

const BUTTON_GROUP =
  'flex gap-3 justify-center animate-[obs-fade-in-up_0.5s_ease-out_0.3s_both] ' +
  'max-[480px]:flex-col';

const PRIMARY_BUTTON =
  'py-3.5 px-8 text-[1.0625rem] font-semibold text-white bg-violet-600 ' +
  'disabled:bg-violet-300 border-0 rounded-lg cursor-pointer disabled:cursor-not-allowed ' +
  'transition-all duration-200 ease-[ease] enabled:hover:bg-violet-700 ' +
  'enabled:hover:-translate-y-px enabled:hover:shadow-[0_4px_12px_rgba(124,58,237,0.3)] ' +
  'enabled:active:translate-y-0 enabled:active:shadow-none';

const SECONDARY_BUTTON =
  'py-3.5 px-8 text-[1.0625rem] font-medium text-gray-500 bg-white border-2 border-gray-200 ' +
  'rounded-lg cursor-pointer transition-all duration-200 ease-[ease] ' +
  'hover:border-violet-600 hover:text-violet-600';

// --- Helpers ---

function downloadImage(url: string, filename: string) {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

// --- Component ---

export const BannerGrid30Step: React.FC<BannerGrid30StepProps> = ({
  sessionId,
  onContinue,
  onBack,
}) => {
  const [banners, setBanners] = useState<BannerResult[]>([]);
  const [completedCount, setCompletedCount] = useState(0);
  const [totalCount] = useState(30);
  const [isPolling, setIsPolling] = useState(true);
  const [timedOut, setTimedOut] = useState(false);
  const user = useSelector((state: RootState) => state.auth.user);
  const mountedRef = useRef(true);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
      }
    };
  }, []);

  // Polling logic
  const poll = useCallback(async () => {
    if (!mountedRef.current || !sessionId) return;

    try {
      const statusData = await getBannerBatchStatus(sessionId);
      if (!mountedRef.current) return;

      setBanners(statusData.banners);
      const completed = statusData.banners.filter(b => b.status === 'completed').length;
      setCompletedCount(completed);

      if (statusData.status === 'completed' || statusData.status === 'failed') {
        setIsPolling(false);
        if (pollTimerRef.current) {
          clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
        }
      }
    } catch {
      // Continue polling on error
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return;

    // Initial poll
    poll();

    const startTime = Date.now();
    pollTimerRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      if (elapsed >= GENERATION_TIMEOUT_MS) {
        setIsPolling(false);
        setTimedOut(true);
        if (pollTimerRef.current) {
          clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
        }
        return;
      }
      poll();
    }, POLL_INTERVAL_MS);

    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [sessionId, poll]);

  const progressPercent = Math.round((completedCount / totalCount) * 100);
  // 3本以上で続行可。生成が止まったら（完了 or タイムアウト）少数でも詰まらせず先へ進める。
  const canContinue = completedCount >= 3 || !isPolling;

  useEffect(() => {
    if (completedCount <= 0) return;

    const completedBanners = banners.filter(b => b.status === 'completed' && !!b.image_url);
    if (completedBanners.length === 0) return;

    const bannerGeneratedEventId = trackMetaBannerGenerated({
      contentIds: completedBanners.slice(0, 3).map((banner, index) => banner.style_key || `banner_${index + 1}`),
      completedCount,
      sessionId,
      user,
    });
    trackXBannerGenerated(bannerGeneratedEventId);
  }, [banners, completedCount, sessionId, user]);

  // Build 30 slots, merging with banner data
  const slots = Array.from({ length: totalCount }, (_, i) => {
    const banner = banners[i] || null;
    const isCompleted = banner?.status === 'completed' && !!banner.image_url;
    return { index: i, banner, isCompleted };
  });

  return (
    <div className={CONTAINER}>
      <h2 className={TITLE}>30本のバナーを生成中...</h2>
      <p className={SUBTITLE}>
        {isPolling
          ? 'AIがあなたのサービスに最適なバナーを大量生成しています'
          : timedOut
            ? `生成に時間がかかっています。${completedCount}本を生成しました。このまま次へ進めます（残りは後から確認できます）。`
            : '生成が完了しました'}
      </p>

      <div className={COUNTER_SECTION}>
        <div className={COUNTER_TEXT}>
          生成済み: <span>{completedCount}/{totalCount}本</span>
        </div>
        <div className={PROGRESS_BAR_TRACK}>
          <div className={PROGRESS_BAR_FILL} style={{ width: `${progressPercent}%` }} />
        </div>
      </div>

      <div className={GRID}>
        {slots.map(({ index, banner, isCompleted }) => (
          <div
            key={index}
            className={isCompleted ? BANNER_CARD_LOADED : BANNER_CARD_PENDING}
          >
            <div className={IMAGE_WRAPPER}>
              {isCompleted && banner?.image_url ? (
                <>
                  <img
                    className={BANNER_IMAGE}
                    src={banner.image_url}
                    alt={`${banner.style_name} - ${index + 1}`}
                    loading="lazy"
                  />
                  <button
                    className={DOWNLOAD_BUTTON}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadImage(banner.image_url!, `banner-${index + 1}.png`);
                    }}
                    title="Download"
                  >
                    <svg viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </>
              ) : (
                <div className={PLACEHOLDER_SLOT}>
                  <span className={PLACEHOLDER_NUMBER}>{index + 1}</span>
                </div>
              )}
            </div>
            <div className={STYLE_BADGE}>
              {banner?.style_name || `Style ${index + 1}`}
            </div>
          </div>
        ))}
      </div>

      <div className={BUTTON_GROUP}>
        <button className={SECONDARY_BUTTON} type="button" onClick={onBack}>
          戻る
        </button>
        <button
          className={PRIMARY_BUTTON}
          type="button"
          onClick={onContinue}
          disabled={!canContinue}
        >
          次へ
        </button>
      </div>
    </div>
  );
};

export default BannerGrid30Step;
